import { StopIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { type ToastOverride, useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { useCancelBeaconRun } from '../../utils/api-client/api-hooks.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import * as styles from './beacon-cancel-button.css.ts'

export type BeaconCancelButtonPureProps = {
  onCancel: () => unknown
  isCancelling: boolean
  /** The platform has cancelled the run; nothing left to press. */
  isCancelled?: boolean
}

/**
 * Stops a running scan, after a confirmation: a cancelled run cannot be resumed, and the findings it had
 * not yet verified are not kept.
 */
export const BeaconCancelButtonPure: React.FC<BeaconCancelButtonPureProps> = ({
  onCancel,
  isCancelling,
  isCancelled = false,
}) => {
  const [isConfirming, setIsConfirming] = useState(false)

  if (isCancelled) {
    return (
      <span className={styles.note} role="status">
        Cancelled
      </span>
    )
  }

  if (!isConfirming) {
    return (
      <DefaultButton buttonType="secondary" size="small" onClick={() => setIsConfirming(true)}>
        Cancel scan
      </DefaultButton>
    )
  }

  return (
    <div className={styles.confirm} role="group" aria-label="Confirm cancelling the scan">
      <span className={styles.question}>Stop this scan? Unverified candidates are not kept.</span>
      <DefaultButton
        buttonType="destructive"
        size="small"
        state={isCancelling ? 'loading' : 'default'}
        loadingLabel="Cancelling"
        onClick={() => onCancel()}
        icon={<StopIcon size={16} weight="fill" />}
      >
        Stop scan
      </DefaultButton>
      <DefaultButton buttonType="secondary" size="small" onClick={() => setIsConfirming(false)}>
        Keep running
      </DefaultButton>
    </div>
  )
}

// The run ended between the page's last read and the press; the hook re-reads it so the page catches up.
const ALREADY_FINISHED_OVERRIDES: Record<number, ToastOverride> = {
  409: { message: 'This Beacon run had already finished, so there was nothing to cancel.' },
}

export type BeaconCancelButtonProps = { runId: string }

/** The platform cancels the run at once and answers with it cancelled, so the page moves on as soon as it lands. */
export const BeaconCancelButton: React.FC<BeaconCancelButtonProps> = ({ runId }) => {
  const { addToast } = useToastService()
  const showError = useShowMutationError<typeof useCancelBeaconRun>({
    prefix: 'Unable to cancel the Beacon scan',
    overrides: ALREADY_FINISHED_OVERRIDES,
  })
  const { mutate, isPending, isSuccess } = useCancelBeaconRun({
    onSuccess: () => addToast({ message: <>Cancelled the Beacon scan</>, variant: 'success' }),
    onError: showError,
  })

  return <BeaconCancelButtonPure onCancel={() => mutate({ runId })} isCancelling={isPending} isCancelled={isSuccess} />
}
